import { getPlatform } from "~/lib/platform";

/**
 * Audio context configuration for platform-specific optimization
 */
export interface AudioContextConfig {
  latencyHint: AudioContextLatencyCategory;
  lookAhead: number;
}

// Singleton AudioContext instance
let audioContext: AudioContext | null = null;
let currentLookAhead = 0.05; // Default lookAhead

/**
 * Get platform-specific audio context configuration
 *
 * Optimizes audio quality and latency for different platforms:
 * - iOS: 50ms lookAhead - balances latency with stability
 * - Android: 80ms lookAhead - broader device compatibility
 * - Desktop/Web: 30ms lookAhead - tighter timing for better performance
 *
 * All platforms use "interactive" latencyHint for low-latency playback
 *
 * @param useWasmSynth - Whether WASM synths are being used (needs more buffer)
 * @returns Platform-specific audio context configuration
 */
export function getAudioContextConfig(useWasmSynth = false): AudioContextConfig {
  const platform = getPlatform();

  // Add extra lookahead for WASM synths to prevent buffer underruns
  const wasmBuffer = useWasmSynth ? 0.02 : 0; // Add 20ms for WASM

  switch (platform) {
    case "ios":
      return {
        latencyHint: "interactive",
        lookAhead: 0.05 + wasmBuffer, // 50-70ms
      };

    case "android":
      return {
        latencyHint: "interactive",
        lookAhead: 0.08 + wasmBuffer, // 80-100ms
      };

    default: // "web" and desktop
      return {
        latencyHint: "interactive",
        lookAhead: 0.03 + wasmBuffer, // 30-50ms
      };
  }
}

/**
 * Get the shared AudioContext instance
 * Creates one if it doesn't exist
 *
 * @returns The shared AudioContext (or null in test environments)
 */
export function getAudioContext(): AudioContext {
  if (!audioContext) {
    // Check if AudioContext is available (not available in test environments like happy-dom)
    if (typeof AudioContext === 'undefined') {
      console.warn(`[AUDIO CONTEXT] AudioContext not available (test environment?)`);
      // Return a mock-like object for test environments
      // Tests should mock AudioContext or use jsdom with Web Audio API support
      throw new Error('AudioContext not available in this environment');
    }

    const config = getAudioContextConfig(true); // Default to WASM mode
    try {
      audioContext = new AudioContext({
        latencyHint: config.latencyHint,
      });
      currentLookAhead = config.lookAhead;
      console.log(`[AUDIO CONTEXT] Created new AudioContext - latencyHint: ${config.latencyHint}, sampleRate: ${audioContext.sampleRate}`);
    } catch (error) {
      // Fallback for environments without AudioContext
      console.warn(`[AUDIO CONTEXT] Failed to create AudioContext:`, error);
      throw error;
    }
  }
  return audioContext;
}

/**
 * Get the current lookAhead value
 * This is used by the AudioScheduler for timing
 */
export function getLookAhead(): number {
  return currentLookAhead;
}

/**
 * Initialize audio context with platform-optimized settings
 *
 * Creates a new AudioContext with latencyHint based on the current platform
 * to optimize audio quality and timing.
 * Must be called in a user gesture context on mobile platforms.
 *
 * @param useWasmSynth - Whether WASM synths are being used (needs more buffer)
 * @returns Promise that resolves when the context is initialized and running (null in test env)
 */
export async function initializeOptimizedAudioContext(useWasmSynth = false): Promise<AudioContext | null> {
  // Check if AudioContext is available (not available in test environments)
  if (typeof AudioContext === 'undefined') {
    console.log(`[AUDIO CONTEXT] AudioContext not available (test environment?), skipping initialization`);
    return null;
  }

  const config = getAudioContextConfig(useWasmSynth);

  // If we already have a context with different settings, we can't change them
  // AudioContext settings are immutable after creation
  if (audioContext) {
    console.log(`[AUDIO CONTEXT] Using existing context - sampleRate: ${audioContext.sampleRate}, state: ${audioContext.state}`);

    // Update lookAhead for scheduler
    currentLookAhead = config.lookAhead;

    // Resume if suspended
    if (audioContext.state === 'suspended') {
      console.log(`[AUDIO CONTEXT] Context suspended, resuming...`);
      await audioContext.resume();
      console.log(`[AUDIO CONTEXT] Context resumed, state: ${audioContext.state}`);
    }

    return audioContext;
  }

  // Create new context
  try {
    console.log(`[AUDIO CONTEXT] Creating new context with optimized settings...`);
    audioContext = new AudioContext({
      latencyHint: config.latencyHint,
    });
    currentLookAhead = config.lookAhead;

    console.log(`[AUDIO CONTEXT] New context created - latencyHint: ${config.latencyHint}, lookAhead: ${config.lookAhead}s, sampleRate: ${audioContext.sampleRate}`);
  } catch (error) {
    console.warn(`[AUDIO CONTEXT] Could not create AudioContext (likely test environment):`, error);
    return null;
  }

  // Resume context if suspended (must be in user gesture)
  if (audioContext.state === 'suspended') {
    console.log(`[AUDIO CONTEXT] Context suspended, resuming...`);
    await audioContext.resume();
    console.log(`[AUDIO CONTEXT] Context resumed, state: ${audioContext.state}`);
  } else {
    console.log(`[AUDIO CONTEXT] Context already ${audioContext.state}`);
  }

  return audioContext;
}

/**
 * Starts audio context if it's currently suspended.
 *
 * iOS Safari requires audio context to be resumed synchronously in the user
 * gesture handler. This utility ensures we only resume when needed,
 * avoiding breaking the user gesture chain on iOS.
 *
 * @returns Promise that resolves when the context is running
 */
export async function startAudioContextIfSuspended(): Promise<void> {
  // Check if AudioContext is available (not available in test environments)
  if (typeof AudioContext === 'undefined') {
    console.log(`[AUDIO CONTEXT] AudioContext not available (test environment?), skipping resume`);
    return;
  }

  try {
    const ctx = getAudioContext();

    if (ctx.state === 'suspended') {
      console.log(`[AUDIO CONTEXT] Context suspended, resuming...`);
      await ctx.resume();
      console.log(`[AUDIO CONTEXT] Context resumed, new state:`, ctx.state);
    } else {
      console.log(`[AUDIO CONTEXT] Context already running (${ctx.state}), skipping resume`);
    }
  } catch (error) {
    console.warn(`[AUDIO CONTEXT] Failed to resume context:`, error);
  }
}

/**
 * @deprecated Use startAudioContextIfSuspended instead
 * Kept for backward compatibility during migration
 */
export const startToneIfSuspended = startAudioContextIfSuspended;

/**
 * Check if AudioWorklet is supported in the current environment
 * Required for WASM synths to work
 * @returns true if AudioWorklet is available
 */
export function isAudioWorkletSupported(): boolean {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return false;
  }

  // Check if AudioWorkletNode constructor exists
  if (typeof AudioWorkletNode === 'undefined') {
    return false;
  }

  // Check if AudioContext has audioWorklet property
  if (typeof AudioContext === 'undefined') {
    return false;
  }

  // Try to check the prototype for audioWorklet
  try {
    // AudioContext.prototype.audioWorklet should exist
    return 'audioWorklet' in AudioContext.prototype;
  } catch {
    return false;
  }
}

/**
 * Check if WebAssembly is supported in the current environment
 * @returns true if WebAssembly is available
 */
export function isWasmSupported(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    // Check for WebAssembly support
    if (typeof WebAssembly !== 'object') {
      return false;
    }

    // Check if we can instantiate a minimal WASM module
    // This is a minimal valid WASM module (just magic bytes + version)
    const minimalWasm = new Uint8Array([0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00]);
    const module = new WebAssembly.Module(minimalWasm);
    return module instanceof WebAssembly.Module;
  } catch {
    return false;
  }
}

/**
 * Check if WASM synths can be used
 * Both AudioWorklet and WebAssembly must be supported
 * @returns true if WASM synths are available
 */
export function canUseWASMSynths(): boolean {
  return isAudioWorkletSupported() && isWasmSupported();
}

/**
 * Dispose of the audio context
 * Call this during cleanup
 */
export async function disposeAudioContext(): Promise<void> {
  if (audioContext) {
    await audioContext.close();
    audioContext = null;
    console.log(`[AUDIO CONTEXT] Context disposed`);
  }
}

/**
 * Reset audio context state (for testing)
 * @internal
 */
export function _resetForTesting(): void {
  audioContext = null;
  currentLookAhead = 0.05;
}
